import classNames from 'classnames';
import Icon from 'components/Icon';
import {Tooltip} from 'components/Tooltip';
import {Typography} from 'components/Typography';
import {formatRating, plural} from 'core/helpers';
import Link from 'components/CustomRoutingLink';
import style from './style.module.scss';
import {IProductShort, IRating} from 'modules/products/types';
import React, {useMemo} from 'react';
import ExpertShield from 'components/Shield/Expert/ExpertShield';
import {OnlyDesktop} from 'components/OnlyDesktop';
import {OnlyMobile} from 'components/OnlyMobile';
import {PLURAL_GRADE_WORDS} from 'modules/products/constants';
import {useProductReviewClickAnalytics} from 'modules/products/hooks/gtag/useProductReviewClickAnalytics';
import {IAnalyticsMeta} from 'core/types';

interface IProps {
    id: IProductShort['id'];
    ratings: IRating[];
    reviewsCount: IProductShort['reviewsCount'];
    url: string;
    isListType?: boolean;
    analytics?: IAnalyticsMeta;
}

export const SimpleWineRating = ({id, ratings, url, reviewsCount, analytics}: IProps) => {
    const productReviewClickGtag = useProductReviewClickAnalytics();

    return (
        <>
            {ratings.map((item) => {
                const {value, code} = item;

                if ('REVIEWS' === code) {
                    return (
                        <Link
                            className={classNames(style.ratingContainer, style.ratingContainerList)}
                            href={`${url}reviews`}
                            key={code}
                            prefetch={false}
                            onClick={productReviewClickGtag(id, analytics, `${url}reviews`)}
                        >
                            <Icon icon={'star'} />
                            <div className={style.ratingCount}>{formatRating(value)}</div>
                            <div className={classNames(style.ratingReviews, style.ratingReviewsList)}>
                                &nbsp;{reviewsCount} {plural(reviewsCount, PLURAL_GRADE_WORDS)}
                            </div>
                        </Link>
                    );
                }

                return null;
            })}
        </>
    );
};

export const ListingCardRating = ({id, ratings, reviewsCount, url, isListType, analytics}: IProps) => {
    const productReviewClickGtag = useProductReviewClickAnalytics();

    return useMemo(() => {
        const sortedRatings = [...ratings];
        // TODO: удалить когда сделают сортировку с бэка
        const reviewsIndex = sortedRatings.findIndex(({code}) => 'REVIEWS' === code);
        if (0 < reviewsIndex) {
            const temp = sortedRatings[0];
            sortedRatings[0] = sortedRatings[reviewsIndex];
            sortedRatings[reviewsIndex] = temp;
        }

        return sortedRatings.map((item) => {
            const {value, code, title} = item;

            if ('REVIEWS' === code) {
                if (isListType) {
                    return null;
                }
                return (
                    <Link
                        className={classNames(style.ratingContainer, style.ratingContainerAnimation)}
                        href={url}
                        key={code}
                        prefetch={false}
                        onClick={productReviewClickGtag(id, analytics, url)}
                    >
                        <Icon icon={'star'} />
                        <div className={style.ratingCount}>{formatRating(value)}</div>
                        <div className={style.ratingReviews}>
                            &nbsp;{reviewsCount} {plural(reviewsCount, PLURAL_GRADE_WORDS)}
                        </div>
                    </Link>
                );
            }

            if (code.toLowerCase() === title.toLowerCase()) {
                return (
                    <React.Fragment key={code}>
                        <OnlyDesktop>
                            <ExpertShield size={'medium'}>
                                {code} {formatRating(value)}
                            </ExpertShield>
                        </OnlyDesktop>
                        <OnlyMobile>
                            <ExpertShield size={'small'}>
                                {code} {formatRating(value)}
                            </ExpertShield>
                        </OnlyMobile>
                    </React.Fragment>
                );
            }

            return (
                <Tooltip
                    key={code}
                    className={style.shieldTooltip}
                    overlay={<Typography className={style.tooltipOverlay}>{title}</Typography>}
                    place={'bottom'}
                >
                    <ExpertShield key={code} size={'small'}>
                        {code} {formatRating(value)}
                    </ExpertShield>
                </Tooltip>
            );
        });
    }, [analytics, id, isListType, productReviewClickGtag, ratings, reviewsCount, url]);
};
